/* Cozyology Talks navigation and related stories. No store data writes or external requests. */
(() => {
  const carousels = new Map();
  const init = (scope = document) => {
    carousels.forEach((dispose, root) => { if (!root.isConnected) dispose(); });
    scope.querySelectorAll('[data-talks-library]').forEach((root) => {
      if (root.dataset.talksReady) return;
      root.dataset.talksReady = 'true';
      const cards = [...root.querySelectorAll('[data-talks-card]')];
      const grid = root.querySelector('[data-talks-grid]');
      const filters = [...root.querySelectorAll('[data-talks-filters] [data-category]')];
      const pagination = root.querySelector('[data-talks-pagination]');
      // Cards have already passed the server's discovery filter.
      filters.forEach((button) => {
        button.hidden = button.dataset.category !== 'all'
          && !cards.some((card) => card.dataset.category === button.dataset.category);
      });
      const categories = filters.filter((button) => !button.hidden).map((button) => button.dataset.category);
      const pageSize = 12;
      const render = (category, requestedPage, updateUrl = false, focus = false) => {
        const selected = categories.includes(category) ? category : 'all';
        // All follows the server's editorial order; categories follow original publication order.
        const ordered = selected === 'all' ? cards : [...cards].sort((a, b) => Number(a.dataset.talksOrder) - Number(b.dataset.talksOrder));
        ordered.forEach((card) => grid.append(card));
        const matches = ordered.filter((card) => selected === 'all' || card.dataset.category === selected);
        const pages = Math.max(1, Math.ceil(matches.length / pageSize));
        const page = Math.min(pages, Math.max(1, Number.parseInt(requestedPage, 10) || 1));
        const visible = new Set(matches.slice((page - 1) * pageSize, page * pageSize));
        cards.forEach((card) => { card.hidden = !visible.has(card); });
        filters.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.category === selected)));
        root.querySelector('[data-talks-empty]').hidden = matches.length > 0;
        const status = root.querySelector('[data-talks-status]');
        status.textContent = matches.length ? `${matches.length} ${matches.length === 1 ? 'story' : 'stories'} · Page ${page} of ${pages}` : 'No stories in this category yet';
        pagination.replaceChildren();
        pagination.hidden = pages < 2;
        const makeButton = (label, target, disabled) => {
          const button = document.createElement('button');
          button.type = 'button'; button.className = 'cz-button cz-button--secondary';
          button.textContent = label; button.disabled = disabled;
          button.addEventListener('click', () => render(selected, target, true, true));
          return button;
        };
        pagination.append(makeButton('Previous', page - 1, page === 1));
        const count = document.createElement('span'); count.textContent = `${page} / ${pages}`;
        pagination.append(count, makeButton('Next', page + 1, page === pages));
        if (updateUrl) {
          const url = new URL(window.location.href);
          url.searchParams.set('talks_category', selected); url.searchParams.set('talks_page', page);
          history.pushState(null, '', url);
        }
        if (focus) {
          status.tabIndex = -1; status.focus({ preventScroll: true });
          status.scrollIntoView({ block: 'center', behavior: 'instant' });
        }
      };
      const restore = () => {
        const params = new URL(window.location.href).searchParams;
        render(params.get('talks_category'), params.get('talks_page'));
      };
      filters.forEach((button) => button.addEventListener('click', () => render(button.dataset.category, 1, true)));
      root.querySelector('[data-talks-reset]').addEventListener('click', () => render('all', 1, true, true));
      root.querySelector('[data-talks-filters]').hidden = false;
      window.addEventListener('popstate', restore);
      restore();
    });
    scope.querySelectorAll('[data-talks-carousel]').forEach((root) => {
      if (root.dataset.talksReady) return;
      root.dataset.talksReady = 'true';
      const track = root.querySelector('[data-talks-track]');
      const previous = root.querySelector('[data-talks-prev]');
      const next = root.querySelector('[data-talks-next]');
      const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const controller = new AbortController();
      const on = (target, type, handler, options = {}) => target.addEventListener(type, handler, { ...options, signal: controller.signal });
      let timer;
      let visible = false;
      let hovered = root.matches(':hover');
      let pressed = false;
      let disposed = false;
      const stop = () => { window.clearTimeout(timer); timer = undefined; };
      const canPlay = () => !disposed && root.isConnected && visible && !document.hidden && !motion.matches && !hovered && !pressed && !root.contains(document.activeElement) && track.scrollWidth > track.clientWidth + 1;
      const schedule = () => {
        stop();
        if (!canPlay()) return;
        timer = window.setTimeout(() => {
          if (!canPlay()) return;
          // Dwell at the end for a full interval, then reset without reverse animation.
          if (track.scrollLeft >= track.scrollWidth - track.clientWidth - 1) {
            track.scrollTo({ left: 0, behavior: 'instant' });
            update();
          } else move(1, true);
          schedule();
        }, 8000);
      };
      const update = () => {
        previous.disabled = track.scrollLeft <= 1;
        next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
      };
      const move = (direction, automatic = false) => {
        const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
        track.scrollBy({ left: direction * (track.clientWidth + gap), behavior: automatic ? 'smooth' : 'instant' });
        update();
      };
      on(previous, 'click', () => { move(-1); schedule(); });
      on(next, 'click', () => { move(1); schedule(); });
      // Restart from the last scroll event, including touch momentum and scrollbar drags.
      on(track, 'scroll', () => { update(); schedule(); }, { passive: true });
      on(root, 'wheel', schedule, { passive: true });
      on(root, 'pointerenter', (event) => { if (event.pointerType !== 'touch') { hovered = true; stop(); } });
      on(root, 'pointerleave', (event) => { if (event.pointerType !== 'touch') { hovered = false; schedule(); } });
      on(root, 'pointerdown', () => { pressed = true; stop(); });
      on(window, 'pointerup', () => { if (pressed) { pressed = false; schedule(); } });
      on(window, 'pointercancel', () => { pressed = false; schedule(); });
      on(root, 'focusin', stop);
      on(root, 'focusout', () => queueMicrotask(schedule));
      on(root, 'keydown', schedule);
      on(track, 'keydown', (event) => {
        if (event.target !== track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') {
          track.scrollTo({ left: event.key === 'Home' ? 0 : track.scrollWidth, behavior: 'instant' });
          update();
        } else move(event.key === 'ArrowLeft' ? -1 : 1);
      });
      on(document, 'visibilitychange', () => { pressed = false; schedule(); });
      on(motion, 'change', () => { update(); schedule(); });
      const resize = new ResizeObserver(() => { update(); schedule(); });
      resize.observe(track);
      const intersection = new IntersectionObserver((entries) => {
        visible = entries[0].isIntersecting && entries[0].intersectionRatio >= .25;
        schedule();
      }, { threshold: [0, .25] });
      intersection.observe(track);
      carousels.set(root, () => {
        disposed = true;
        stop();
        controller.abort();
        resize.disconnect();
        intersection.disconnect();
        delete root.dataset.talksReady;
        carousels.delete(root);
      });
      root.querySelector('[data-talks-carousel-controls]').hidden = false;
      update();
    });
    scope.querySelectorAll('[data-talks-prose]').forEach((prose) => {
      const toc = prose.parentElement.querySelector('[data-talks-toc]');
      if (!toc || toc.dataset.ready) return;
      toc.dataset.ready = 'true';
      const headings = [...prose.querySelectorAll('h2')];
      // Preserve a table of contents already supplied in the original article.
      const headingIds = new Set(headings.map((heading) => heading.id).filter(Boolean));
      const existingTocLinks = [...prose.querySelectorAll('a[href^="#"]')]
        .filter((link) => link.textContent.trim() && headingIds.has(link.getAttribute('href').slice(1)));
      if (headings.length < 4 || existingTocLinks.length > 1) return;
      const title = document.createElement('p'); title.textContent = 'In this article'; toc.append(title);
      headings.forEach((heading, index) => {
        if (!heading.id) heading.id = `talks-section-${index + 1}`;
        const link = document.createElement('a'); link.href = `#${heading.id}`; link.textContent = heading.textContent;
        toc.append(link);
      });
      toc.hidden = false;
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    carousels.forEach((dispose, root) => { if (event.target.contains(root)) dispose(); });
  });
  window.addEventListener('pagehide', () => carousels.forEach((dispose) => dispose()));
  window.addEventListener('pageshow', (event) => { if (event.persisted) init(); });
})();
